import { FileUpload, FileUploadDropzone } from '@/components/ui/file-upload'
import { Trash2, UploadIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { uploadFile } from 'app/frontend/base/api/team'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { useFormContext } from 'react-hook-form'
import { FieldProps } from 'app/frontend/base/components/form-fields/types'

const FileInput = ({ inputClassName, name }: FieldProps<string>) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const [rejection, setRejection] = useState<{
    fileName: string
    message: string
  } | null>(null)

  const form = useFormContext()
  const imageUrl = form.watch(name)

  const handleImageUpload = async (files: File[]) => {
    setRejection(null)
    const response = await uploadFile(team.id, files[0])
    form.setValue(name, response.url)
  }

  return (
    <>
      <Tabs
        defaultValue={imageUrl?.includes('wowaudit.com') ? 'upload' : 'url'}
      >
        <TabsList>
          <TabsTrigger value='url'>{t('news.image-url')}</TabsTrigger>
          <TabsTrigger value='upload'>{t('news.image-upload')}</TabsTrigger>
        </TabsList>
        <TabsContent value='url'>
          <Input
            placeholder={'Image URL'}
            className={cn(inputClassName, 'mb-20')}
            value={imageUrl || ''}
            onChange={(e) => form.setValue(name, e.target.value)}
          />
        </TabsContent>
        <TabsContent value='upload'>
          <FileUpload
            value={[]}
            onValueChange={handleImageUpload}
            onFileReject={(file, message) =>
              setRejection({ fileName: file.name, message })
            }
            accept='image/*'
            maxFiles={1}
            maxSize={5 * 1024 * 1024}
            className='w-full'
          >
            {imageUrl ? (
              <div className='h-[120px] flex items-center justify-between gap-2.5 overflow-hidden rounded-md border p-3 bg-muted'>
                <img
                  src={imageUrl}
                  alt='Image'
                  className='h-full w-auto max-w-full flex-1 min-w-0 object-contain'
                />
                <Button
                  variant='ghost'
                  size='icon'
                  type='button'
                  className='shrink-0'
                  onClick={() => form.setValue(name, null)}
                >
                  <Trash2 className='h-4 w-4 text-destructive' />
                </Button>
              </div>
            ) : (
              <FileUploadDropzone className='h-[120px] cursor-pointer bg-muted hover:bg-muted/70'>
                <div className='flex flex-col items-center gap-2 text-center'>
                  <UploadIcon className='h-8 w-8 text-muted-foreground' />
                  <div className='text-sm text-muted-foreground'>
                    <span className='font-semibold'>Click to upload</span> or
                    drag and drop
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    Images only, up to 5MB
                  </div>
                </div>
              </FileUploadDropzone>
            )}
            {rejection && (
              <Alert variant='destructive' className='mt-3'>
                <AlertTitle>Upload rejected</AlertTitle>
                <AlertDescription>
                  {rejection.fileName}: {rejection.message}
                </AlertDescription>
              </Alert>
            )}
          </FileUpload>
        </TabsContent>
      </Tabs>
    </>
  )
}

export default FileInput
